/**
 * Comenzile (redesign 21.10.2026, macheta https://claude.ai/artifact/HidXUkBPmk1F8kvHVDBSat).
 *
 * Madalin: antetul lipit (titlu + stari + perioada + data + client + agent + factura) acoperea
 * lista la derulare si „arata ca un bug vizual". Acum: carduri cu ce cere atentie, o singura bara
 * de filtre (nelipita: lipita, arata ciudat), stari cu numar, tabel sortabil. Datele din lista sunt
 * aceleasi ca inainte. Clic pe o comanda: pagina ei.
 *
 * Filtrele stau in adresa paginii: inapoi din comanda, lista e exact cum ai lasat-o.
 */
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search, X } from 'lucide-react'
import { api } from '../../lib/api'
import { SpvInvoiceViewer } from '../../components/SpvInvoiceViewer'
import { SpvDebugModal } from '../../components/orders/SpvDebugModal'
import { useAuthStore } from '../../stores/auth'
import { Button } from '../../components/ui/Button'
import { SearchSelect } from '../../components/ui/SearchSelect'
import { Pagination } from '../../components/ui/Pagination'
import { Pastila } from '../../components/ui/Pastila'
import { CarduriSelector } from '../../components/ui/CarduriSelector'
import { ChipuriFiltru } from '../../components/ui/ChipuriFiltru'
import { CapSortabil, useSortare } from '../../components/ui/Sortare'
import { FiltruPerioada, numePerioada } from '../../components/ui/FiltruPerioada'
import type { Ton } from '../../components/ui/TonuriB4L'
import { useClientPagination } from '../../hooks/useClientPagination'
import { AwbInLista, StareAwb, type AwbScurt } from '../../components/orders/AwbComanda'
import { linkUrmarire } from '../../components/ui/LinkAwb'
import { LivratFaraScanare } from '../../components/ui/LivratFaraScanare'
import { ETICHETE_CIFRE, esteComandaAprobata, valoareComenziAprobate } from '@b4l/shared'

export type OrderStatus =
  | 'ciorna'
  | 'asteapta_aprobare'
  | 'respinsa'
  | 'aprobata'
  | 'trimisa_ea'
  | 'eroare_ea'
  | 'anulata'

/** Starea comenzii: nume si ton, aceleasi in lista si in pagina comenzii. */
export const STATUS_META: Record<OrderStatus, { label: string; ton: Ton }> = {
  ciorna: { label: 'Ciornă', ton: 'gri' },
  asteapta_aprobare: { label: 'Așteaptă aprobare', ton: 'rosu' },
  respinsa: { label: 'Respinsă', ton: 'galben' },
  aprobata: { label: 'Aprobată', ton: 'verde' },
  trimisa_ea: { label: 'Trimisă în EA', ton: 'albastru' },
  eroare_ea: { label: 'Eroare EA', ton: 'rosu' },
  anulata: { label: 'Anulată', ton: 'gri' },
}

/** Cum se citește suma unei stări: „1.200 lei în așteptare”. */
const SUMA_STARE: Record<OrderStatus, string> = {
  ciorna: 'în ciornă',
  asteapta_aprobare: 'în așteptare',
  respinsa: 'respinse',
  aprobata: 'aprobate',
  trimisa_ea: 'trimise în EA',
  eroare_ea: 'cu eroare EA',
  anulata: 'anulate',
}

export function StatusBadge({ status }: { status: OrderStatus }) {
  const m = STATUS_META[status] ?? { label: status, ton: 'gri' as const }
  return <Pastila ton={m.ton}>{m.label}</Pastila>
}

interface OrderRow {
  id: number
  code: string
  status: OrderStatus
  orderDiscountPct: string
  totalWithVat: string
  totalNoVat: string
  needsApproval: boolean
  createdAt: string
  updatedAt: string
  notes: string | null
  clientLocation: string | null
  client: { id: number; name: string; agentName: string | null } | null
  createdBy: { id: number; name: string } | null
  /** AWB-urile comenzii, din expeditiile curierilor; cel mai relevant primul. */
  awb: AwbScurt[]
}

type Card = 'toate' | 'aprobare' | 'eroare' | 'faraFactura' | 'colete'
type Factura = '' | 'cu' | 'fara'
type Livrare = '' | 'fara_awb' | 'nepreluat' | 'in_tranzit' | 'intarziat' | 'livrat' | 'retur'
type Coloana =
  | 'cod'
  | 'client'
  | 'agent'
  | 'stare'
  | 'discount'
  | 'total'
  | 'data'
  | 'creat'
  | 'obs'
  | 'factura'
  | 'awb'

const ORDINE_STARI: OrderStatus[] = [
  'ciorna',
  'asteapta_aprobare',
  'aprobata',
  'trimisa_ea',
  'eroare_ea',
  'respinsa',
  'anulata',
]

/** „2026-10-01" — prima zi a lunii in curs, in ora locala. */
function primaZiDinLuna(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}
function ultimaZiDinLuna(): string {
  const d = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const lei = (v: number) =>
  v.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const zileDe = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)

/** Felul livrarii, dupa primul AWB al comenzii — aceleasi stari ca la Rambursuri curieri. */
function felLivrare(o: OrderRow): Livrare {
  // Intarziat daca ORICARE AWB e intarziat — aceeasi socoteala ca pe cardul „Colete de urmarit".
  if ((o.awb ?? []).some((x) => x.intarziere != null)) return 'intarziat'
  // AWB-urile anulate nu sunt livrari: comanda trimisa fara alt AWB e „fara AWB".
  const a = (o.awb ?? []).find((x) => x.stare !== 'anulat')
  if (!a) return o.status === 'trimisa_ea' ? 'fara_awb' : ''
  switch (a.stare) {
    case 'livrat':
      return 'livrat'
    case 'retur':
    case 'in_retur':
    case 'refuzat':
      return 'retur'
    case 'nepreluat':
      return 'nepreluat'
    case 'in_tranzit':
      return 'in_tranzit'
    // Inchis de mana, inlocuit, neurmarit: nicio categorie de livrare; se vad la „oricare".
    default:
      return ''
  }
}

export function ComenziPage() {
  const navigate = useNavigate()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('orders:write')
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin')
  const [debugFor, setDebugFor] = useState<{ id: number; code: string } | null>(null)
  const [viewer, setViewer] = useState<{
    inboundId: number
    orderId: number
    docId: string | null
  } | null>(null)
  const [filtreMobil, setFiltreMobil] = useState(false)

  /*
    Ecranul se deschide pe LUNA CURENTA, nu pe tot istoricul (Alexandru: „nu cred ca are rost sa
    se vada toate comenzile de la inceput"). „Toata perioada" ramane la un clic distanta.
  */
  const [p, setP] = useSearchParams()
  const de = p.has('de') ? (p.get('de') ?? '') : primaZiDinLuna()
  const pana = p.has('pana') ? (p.get('pana') ?? '') : ultimaZiDinLuna()
  const clientId = p.get('client') ? Number(p.get('client')) : null
  const agentName = p.get('agent') ?? ''
  const card = (p.get('card') ?? 'toate') as Card
  const stare = (p.get('stare') ?? '') as OrderStatus | ''
  const factura = (p.get('factura') ?? '') as Factura
  const livrare = (p.get('livrare') ?? '') as Livrare
  const q = p.get('q') ?? ''
  const pune = (schimbari: Record<string, string | null>) =>
    setP(
      (vechi) => {
        const n = new URLSearchParams(vechi)
        for (const [k, v] of Object.entries(schimbari)) {
          /*
            Perioada goala e o alegere („Toata perioada"), nu lipsa ei: `de=` ramane in adresa. Daca
            s-ar sterge, ecranul ar reveni la luna curenta, iar toata perioada n-ar mai putea fi aleasa.
          */
          if ((k === 'de' || k === 'pana') && v != null) n.set(k, v)
          else if (v == null || v === '') n.delete(k)
          else n.set(k, v)
        }
        return n
      },
      { replace: true },
    )

  // Serverul filtreaza perioada, clientul si agentul; restul se taie aici, ca numerele de pe
  // carduri si stari sa fie mereu ale aceleiasi liste.
  const params = new URLSearchParams()
  if (clientId) params.set('clientId', String(clientId))
  if (agentName) params.set('agentName', agentName)
  if (de) params.set('dateFrom', de)
  if (pana) params.set('dateTo', pana)
  const qs = params.toString()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['orders', qs],
    queryFn: async () =>
      (await api.get<{ orders: OrderRow[] }>(`/orders${qs ? `?${qs}` : ''}`)).data.orders,
  })
  const { data: filterOpts } = useQuery({
    queryKey: ['orders-filter-options'],
    queryFn: async () =>
      (
        await api.get<{ clients: { id: number; name: string }[]; agents: string[] }>(
          '/orders/filter-options',
        )
      ).data,
  })
  const toate = useMemo(() => data ?? [], [data])

  /*
    Facturile SPV pentru comenzile trimise, in transe de 100 (ruta accepta maximum 200 de coduri).
    La peste doua sute de comenzi cererea pica, iar ecranul scria „nu apare in SPV" pe toate
    randurile, desi facturile erau legate corect (Alexandru).
  */
  const sentIds = useMemo(
    () => toate.filter((o) => o.status === 'trimisa_ea').map((o) => o.id),
    [toate],
  )
  const { data: spvMap, isError: spvEroare } = useQuery({
    queryKey: ['orders-spv', sentIds],
    queryFn: async () => {
      const transe: number[][] = []
      for (let i = 0; i < sentIds.length; i += 100) transe.push(sentIds.slice(i, i + 100))
      const parti = await Promise.all(
        transe.map(
          async (ids) =>
            (
              await api.post<{
                invoices: Record<string, { inboundId: number; docId: string | null }>
              }>('/orders/spv-invoices', { ids })
            ).data.invoices,
        ),
      )
      return Object.assign({}, ...parti) as Record<
        string,
        { inboundId: number; docId: string | null }
      >
    },
    enabled: sentIds.length > 0,
  })
  const spvFor = (id: number) => spvMap?.[String(id)]
  // „Fara factura" se stie doar dupa ce a raspuns SPV-ul; pana atunci nu se numara nimic.
  const spvStiut = sentIds.length === 0 || (spvMap != null && !spvEroare)
  const faraFactura = (o: OrderRow) => o.status === 'trimisa_ea' && spvStiut && !spvFor(o.id)

  // ── Ce cere atentie: cardurile de sus, ca filtre ──────────────────────────────────────────
  const peCard: Record<Card, (o: OrderRow) => boolean> = {
    toate: () => true,
    aprobare: (o) => o.status === 'asteapta_aprobare',
    eroare: (o) => o.status === 'eroare_ea',
    faraFactura,
    // Aceeasi regula ca la Rambursuri curieri: preluat de curier, nelivrat de 4+ zile lucratoare.
    colete: (o) => (o.awb ?? []).some((a) => a.intarziere != null),
  }
  const deAprobat = toate.filter(peCard.aprobare)
  const ceaMaiVeche = deAprobat.reduce<number | null>(
    (m, o) => Math.max(m ?? 0, zileDe(o.updatedAt ?? o.createdAt)),
    null,
  )
  // Totalul afisat = doar comenzile aprobate (registrul cifrelor, V17); lista ramane completa.
  const aprobate = valoareComenziAprobate(toate)
  const carduri = [
    {
      cheie: 'toate' as const,
      eticheta: 'Toate din perioadă',
      valoare: toate.length,
      detaliu: (
        <span title={ETICHETE_CIFRE.valoareComenziAprobate.detaliu}>
          {lei(aprobate.valoare)} lei aprobate, cu TVA
        </span>
      ),
      ton: 'albastru' as const,
    },
    {
      cheie: 'aprobare' as const,
      eticheta: 'Așteaptă aprobare',
      valoare: deAprobat.length,
      detaliu:
        deAprobat.length === 0
          ? 'nimic de aprobat'
          : ceaMaiVeche
            ? `cea mai veche de ${ceaMaiVeche} ${ceaMaiVeche === 1 ? 'zi' : 'zile'}`
            : 'discount peste prag',
      ton: 'rosu' as const,
    },
    {
      cheie: 'eroare' as const,
      eticheta: 'Eroare EA',
      valoare: toate.filter(peCard.eroare).length,
      detaliu: 'de retrimis sau verificat în EA',
      ton: 'rosu' as const,
    },
    {
      cheie: 'faraFactura' as const,
      eticheta: 'Trimise, fără factură',
      valoare: spvStiut ? toate.filter(faraFactura).length : '…',
      detaliu: spvEroare ? 'SPV-ul n-a putut fi verificat' : 'nu apare încă în SPV',
      ton: 'galben' as const,
    },
    {
      cheie: 'colete' as const,
      eticheta: 'Colete de urmărit',
      valoare: toate.filter(peCard.colete).length,
      detaliu: 'preluate, nelivrate de 4+ zile lucrătoare',
      ton: 'galben' as const,
    },
  ]

  // ── Lista: card → stare → factura → livrare → cautare → sortare ───────────────────────────
  const dupaCard = toate.filter(peCard[card] ?? peCard.toate)
  const numarStare = (s: OrderStatus) => dupaCard.filter((o) => o.status === s).length
  const cautat = q.trim().toLowerCase()
  const filtrate = dupaCard.filter((o) => {
    if (stare && o.status !== stare) return false
    if (factura === 'cu' && !spvFor(o.id)) return false
    if (factura === 'fara' && !faraFactura(o)) return false
    if (livrare && felLivrare(o) !== livrare) return false
    if (cautat) {
      const text = [
        o.code,
        o.client?.name,
        o.clientLocation,
        o.notes,
        o.client?.agentName,
        o.createdBy?.name,
        spvFor(o.id)?.docId,
        ...(o.awb ?? []).map((a) => a.awbNo),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (!text.includes(cautat)) return false
    }
    return true
  })

  const sortare = useSortare<Coloana>('data', {
    cod: 'desc',
    client: 'asc',
    agent: 'asc',
    stare: 'asc',
    creat: 'asc',
    obs: 'asc',
    factura: 'asc',
    awb: 'asc',
  })
  const ordonate = sortare.ordoneaza(filtrate, (o, k) => {
    switch (k) {
      case 'cod':
        return o.code
      case 'client':
        return o.client?.name ?? null
      case 'agent':
        return o.client?.agentName ?? null
      case 'stare':
        return ORDINE_STARI.indexOf(o.status)
      case 'discount':
        return Number(o.orderDiscountPct)
      case 'total':
        return Number(o.totalWithVat)
      case 'data':
        return new Date(o.createdAt)
      case 'creat':
        return o.createdBy?.name ?? null
      case 'obs':
        return o.notes ?? null
      case 'factura':
        return spvFor(o.id)?.docId ?? null
      case 'awb':
        return o.awb?.[0]?.awbNo ?? null
    }
  })
  const pag = useClientPagination(ordonate, 50)
  // Orice filtru, cautare sau sortare noua porneste de la pagina 1.
  const cheieFiltre = [qs, card, stare, factura, livrare, q, sortare.cheie, sortare.directie].join(
    '|',
  )
  const { setPage } = pag
  useEffect(() => {
    setPage(0)
  }, [cheieFiltre, setPage])
  /*
    Totalul listei. Fara filtru pe stare: doar comenzile aprobate, ca la cardul de sus (V17) —
    lista le arata pe toate. Cu o stare aleasa (din filtru sau din card), suma comenzilor din
    starea aceea, cu numele ei: altfel „Așteaptă aprobare” ar arata mereu „0 lei aprobate”.
  */
  const stareTotal: OrderStatus | '' =
    stare || (card === 'aprobare' ? 'asteapta_aprobare' : card === 'eroare' ? 'eroare_ea' : '')
  const deAdunat = stareTotal ? filtrate : filtrate.filter((o) => esteComandaAprobata(o.status))
  const totalCuTva = deAdunat.reduce((s, o) => s + Number(o.totalWithVat), 0)
  const totalFaraTva = deAdunat.reduce((s, o) => s + Number(o.totalNoVat), 0)
  const etichetaTotal = stareTotal ? SUMA_STARE[stareTotal] : 'aprobate'

  const filtreActive =
    Boolean(clientId || agentName || factura || livrare || stare || cautat) || card !== 'toate'
  const reseteaza = () =>
    pune({
      client: null,
      agent: null,
      factura: null,
      livrare: null,
      stare: null,
      q: null,
      card: null,
    })

  const openInvoice = (
    e: React.MouseEvent,
    orderId: number,
    inboundId: number,
    docId?: string | null,
  ) => {
    e.stopPropagation()
    setViewer({ inboundId, orderId, docId: docId ?? null })
  }

  const selectCls =
    'rounded-full border border-b4l-border bg-b4l-surface px-3 py-1.5 text-[13px] text-b4l-text hover:bg-b4l-bg-soft'
  const selectActiv = 'border-b4l-blue'

  const celulaFactura = (o: OrderRow) =>
    o.status !== 'trimisa_ea' ? (
      <span className="text-b4l-text-dim">—</span>
    ) : spvFor(o.id) ? (
      <button
        type="button"
        onClick={(e) => openInvoice(e, o.id, spvFor(o.id)!.inboundId, spvFor(o.id)!.docId)}
        className="font-medium text-b4l-blue-dark underline decoration-dotted underline-offset-2 hover:decoration-solid"
        title="Deschide factura din SPV"
      >
        {spvFor(o.id)!.docId ?? 'Factura'}
      </button>
    ) : (
      <span className="inline-flex items-center gap-1.5">
        <span className={spvEroare ? 'text-xs text-b4l-text-dim' : 'text-xs text-b4l-amber-dark'}>
          {spvEroare ? 'neverificat' : 'lipsă în SPV'}
        </span>
        {isAdmin && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setDebugFor({ id: o.id, code: o.code })
            }}
            className="rounded border border-b4l-border px-1.5 py-0.5 text-[10px] text-b4l-text-muted hover:bg-b4l-bg-soft"
            title="De ce nu se leagă factura?"
          >
            diag
          </button>
        )}
      </span>
    )

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="grid gap-1">
          <h1 className="text-2xl font-semibold text-b4l-text">Comenzi</h1>
          <p className="text-sm text-b4l-text-muted">
            {numePerioada(de, pana)}
            {data
              ? ` · ${toate.length} ${toate.length === 1 ? 'comandă' : 'comenzi'} · ${lei(aprobate.valoare)} lei aprobate, cu TVA (comenzi, nu facturi)`
              : ''}
            . Clic pe o comandă: pagina ei.
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => navigate('/operational/comenzi/nou')}>+ Comandă nouă</Button>
        )}
      </header>

      <div>
        <CarduriSelector
          carduri={carduri}
          ales={card}
          onAlege={(k) => pune({ card: k === 'toate' || k === card ? null : k, stare: null })}
          eticheta="Ce cere atenție"
        />
      </div>

      <section
        aria-label="Comenzile"
        className="rounded-xl border border-b4l-border bg-b4l-surface"
      >
        {/*
          Bara de filtre NU mai ramane lipita sus (Madalin, 21.10.2026: „arata ciudat"). Lipita, se
          oprea sub captuseala lui <main>, iar randurile se vedeau trecand prin fasia de deasupra.
        */}
        <div className="grid gap-2 rounded-t-xl border-b border-b4l-border bg-b4l-surface px-3 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex min-w-[220px] flex-1 items-center gap-2 rounded-lg border border-b4l-border bg-b4l-bg-soft px-2.5 py-1.5 text-sm text-b4l-text-muted">
              <Search size={15} aria-hidden="true" />
              <input
                value={q}
                onChange={(e) => pune({ q: e.target.value })}
                placeholder="Caută cod, client, punct de lucru, factură sau AWB"
                aria-label="Caută comenzi"
                className="min-w-0 flex-1 bg-transparent text-b4l-text outline-none placeholder:text-b4l-text-dim"
              />
              {q && (
                <button
                  type="button"
                  aria-label="Golește căutarea"
                  onClick={() => pune({ q: null })}
                >
                  <X size={14} />
                </button>
              )}
            </label>
            <button
              type="button"
              onClick={() => setFiltreMobil((v) => !v)}
              className="rounded-lg border border-b4l-border px-3 py-1.5 text-sm sm:hidden"
              aria-expanded={filtreMobil}
            >
              Filtre{filtreActive ? ' ·' : ''}
            </button>
            <div
              className={`${filtreMobil ? 'flex' : 'hidden'} w-full flex-wrap items-center gap-2 sm:flex sm:w-auto`}
            >
              <FiltruPerioada de={de} pana={pana} onChange={(a, b) => pune({ de: a, pana: b })} />
              <div className="w-56">
                <SearchSelect
                  options={(filterOpts?.clients ?? []).map((c) => ({ value: c.id, label: c.name }))}
                  value={clientId}
                  onChange={(v) => pune({ client: v ? String(v) : null })}
                  placeholder="Client: toți"
                />
              </div>
              {(filterOpts?.agents ?? []).length > 1 && (
                <select
                  value={agentName}
                  onChange={(e) => pune({ agent: e.target.value })}
                  aria-label="Agent"
                  className={`${selectCls} ${agentName ? selectActiv : ''}`}
                >
                  <option value="">Agent: toți</option>
                  {(filterOpts?.agents ?? []).map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              )}
              {/*
                „Fara" = TRIMISA IN ERP si fara factura sosita — nu orice comanda fara factura: o
                ciorna n-are factura prin definitie, iar amestecata aici ar ineca exact cazurile
                care cer atentie.
              */}
              <select
                value={factura}
                onChange={(e) => pune({ factura: e.target.value })}
                aria-label="Factură"
                className={`${selectCls} ${factura ? selectActiv : ''}`}
              >
                <option value="">Factură: oricare</option>
                <option value="cu">Cu factură în SPV</option>
                <option value="fara">Trimise, fără factură</option>
              </select>
              <select
                value={livrare}
                onChange={(e) => pune({ livrare: e.target.value })}
                aria-label="Livrare"
                className={`${selectCls} ${livrare ? selectActiv : ''}`}
              >
                <option value="">Livrare: oricare</option>
                <option value="fara_awb">Trimise, fără AWB</option>
                <option value="nepreluat">AWB nepreluat</option>
                <option value="in_tranzit">În tranzit</option>
                <option value="intarziat">Întârziate (4+ zile lucrătoare)</option>
                <option value="livrat">Livrate</option>
                <option value="retur">Retur sau refuzate</option>
              </select>
              {filtreActive && (
                <button
                  type="button"
                  onClick={reseteaza}
                  className="text-[13px] text-b4l-blue-dark hover:underline"
                >
                  Resetează filtrele
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <ChipuriFiltru
              eticheta="Stare"
              chipuri={[
                { cheie: '' as const, eticheta: 'Toate', numar: dupaCard.length },
                ...ORDINE_STARI.map((s) => ({
                  cheie: s,
                  eticheta: STATUS_META[s].label,
                  numar: numarStare(s),
                })),
              ]}
              ales={stare}
              onAlege={(k) => pune({ stare: k })}
            />
            <span className="text-[13px] text-b4l-text-muted">
              {filtrate.length} {filtrate.length === 1 ? 'comandă' : 'comenzi'} ·{' '}
              <span
                className="font-semibold text-b4l-text"
                title={stareTotal ? undefined : ETICHETE_CIFRE.valoareComenziAprobate.detaliu}
              >
                {lei(totalCuTva)} lei {etichetaTotal}
              </span>
            </span>
          </div>
        </div>

        {isLoading ? (
          <p className="p-4 text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : isError ? (
          <p className="p-4 text-sm text-b4l-red-dark">
            Comenzile nu s-au putut încărca. Reîncarcă pagina.
          </p>
        ) : (factura || card === 'faraFactura') && !spvStiut ? (
          <p className="p-4 text-sm text-b4l-text-muted">
            {spvEroare
              ? 'Facturile din SPV nu s-au putut verifica acum, deci filtrul de factură nu poate da un răspuns sigur. Reîncarcă pagina peste puțin.'
              : 'Se verifică facturile în SPV…'}
          </p>
        ) : filtrate.length === 0 ? (
          <div className="grid justify-items-start gap-2 p-4 text-sm text-b4l-text-muted">
            <span>
              {toate.length === 0
                ? `Nicio comandă în ${numePerioada(de, pana).toLowerCase()}.`
                : 'Nicio comandă pentru filtrele alese.'}
            </span>
            {filtreActive && (
              <button
                type="button"
                onClick={reseteaza}
                className="text-b4l-blue-dark hover:underline"
              >
                Resetează filtrele
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Telefon: fise */}
            <ul className="grid gap-2 p-3 sm:hidden">
              {pag.paginated.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/operational/comenzi/${o.id}`, { state: { dinLista: true } })
                    }
                    className="grid w-full gap-1.5 rounded-xl border border-b4l-border bg-b4l-surface p-3 text-left"
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="font-semibold">{o.code}</span>
                      <StatusBadge status={o.status} />
                    </span>
                    <span className="text-sm">
                      {o.client?.name ?? '—'}
                      {o.clientLocation && (
                        <span className="text-b4l-text-muted"> · {o.clientLocation}</span>
                      )}
                    </span>
                    <span className="flex justify-between text-[13px] text-b4l-text-muted">
                      <span>
                        {new Date(o.createdAt).toLocaleDateString('ro-RO')}
                        {o.client?.agentName ? ` · ${o.client.agentName}` : ''}
                        {Number(o.orderDiscountPct) > 0 ? ` · -${Number(o.orderDiscountPct)}%` : ''}
                      </span>
                      <span className="font-semibold text-b4l-text">
                        {lei(Number(o.totalWithVat))} lei
                      </span>
                    </span>
                    {o.notes && (
                      <span className="truncate text-xs text-b4l-text-muted" title={o.notes}>
                        Obs: {o.notes}
                      </span>
                    )}
                    <span className="flex flex-wrap items-center gap-2 text-xs text-b4l-text-muted">
                      {o.status === 'trimisa_ea' &&
                        (spvFor(o.id) ? (
                          <span
                            role="link"
                            tabIndex={0}
                            onClick={(e) =>
                              openInvoice(e, o.id, spvFor(o.id)!.inboundId, spvFor(o.id)!.docId)
                            }
                            className="font-medium text-b4l-blue-dark"
                          >
                            Factura {spvFor(o.id)!.docId ?? 'SPV'}
                          </span>
                        ) : (
                          <span className="text-b4l-amber-dark">
                            {spvEroare ? 'Factura neverificată' : 'Factura lipsă în SPV'}
                          </span>
                        ))}
                      {o.awb?.[0] && (
                        <>
                          {/* Randul e un <button>: un <a> inauntru n-ar fi valid, deci link „de mana". */}
                          <span
                            role="link"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation()
                              const u = linkUrmarire(o.awb[0]!.courier, o.awb[0]!.awbNo)
                              if (u) window.open(u, '_blank', 'noopener,noreferrer')
                            }}
                            className="font-mono text-b4l-blue-dark underline decoration-dotted"
                          >
                            AWB {o.awb[0].awbNo}
                          </span>
                          <StareAwb a={o.awb[0]} />
                          {o.awb[0].faraScanareLivrare && (
                            <LivratFaraScanare semnat={o.awb[0].deliveredTo} />
                          )}
                          {o.awb.length > 1 && <span>+{o.awb.length - 1}</span>}
                        </>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {/* Desktop: tabel */}
            <div className="hidden overflow-x-auto sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-b4l-border bg-b4l-bg-soft text-left text-[12px] text-b4l-text-muted">
                    <th className="px-3 py-2">
                      <CapSortabil sortare={sortare} coloana="cod">
                        Cod
                      </CapSortabil>
                    </th>
                    <th className="px-3 py-2">
                      <CapSortabil sortare={sortare} coloana="client">
                        Client · punct de lucru
                      </CapSortabil>
                    </th>
                    <th className="px-3 py-2">
                      <CapSortabil sortare={sortare} coloana="agent">
                        Agent
                      </CapSortabil>
                    </th>
                    <th className="px-3 py-2">
                      <CapSortabil sortare={sortare} coloana="stare">
                        Stare
                      </CapSortabil>
                    </th>
                    <th className="px-3 py-2 text-right">
                      <CapSortabil sortare={sortare} coloana="discount" dreapta>
                        Disc.
                      </CapSortabil>
                    </th>
                    <th className="px-3 py-2 text-right">
                      <CapSortabil sortare={sortare} coloana="total" dreapta>
                        Total cu TVA
                      </CapSortabil>
                    </th>
                    <th className="px-3 py-2">
                      <CapSortabil sortare={sortare} coloana="data">
                        Data
                      </CapSortabil>
                    </th>
                    <th className="px-3 py-2">
                      <CapSortabil sortare={sortare} coloana="creat">
                        Creat de
                      </CapSortabil>
                    </th>
                    <th className="px-3 py-2">
                      <CapSortabil sortare={sortare} coloana="obs">
                        Observații
                      </CapSortabil>
                    </th>
                    <th className="px-3 py-2">
                      <CapSortabil sortare={sortare} coloana="factura">
                        Factură
                      </CapSortabil>
                    </th>
                    <th className="px-3 py-2">
                      <CapSortabil sortare={sortare} coloana="awb">
                        AWB
                      </CapSortabil>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pag.paginated.map((o) => (
                    <tr
                      key={o.id}
                      onClick={() =>
                        navigate(`/operational/comenzi/${o.id}`, { state: { dinLista: true } })
                      }
                      className="cursor-pointer border-b border-b4l-border align-middle last:border-0 hover:bg-b4l-bg-soft"
                    >
                      <td className="whitespace-nowrap px-3 py-2.5 font-semibold tabular-nums">
                        {o.code}
                      </td>
                      <td className="max-w-[20rem] px-3 py-2.5">
                        <div className="truncate text-b4l-text">{o.client?.name ?? '—'}</div>
                        {o.clientLocation && (
                          <div
                            className="truncate text-xs text-b4l-text-muted"
                            title="Punct de lucru"
                          >
                            {o.clientLocation}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-b4l-text-muted">
                        {o.client?.agentName ?? '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        <StatusBadge status={o.status} />
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-b4l-text-muted">
                        {Number(o.orderDiscountPct) > 0 ? `-${Number(o.orderDiscountPct)}%` : '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right font-semibold tabular-nums">
                        {lei(Number(o.totalWithVat))} lei
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-b4l-text-muted">
                        {new Date(o.createdAt).toLocaleDateString('ro-RO')}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-b4l-text-muted">
                        {o.createdBy?.name ?? '—'}
                      </td>
                      <td
                        className="max-w-[14rem] truncate px-3 py-2.5 text-b4l-text-muted"
                        title={o.notes ?? undefined}
                      >
                        {o.notes || '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5">{celulaFactura(o)}</td>
                      <td className="px-3 py-2.5">
                        <AwbInLista awb={o.awb ?? []} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-b4l-border px-3 py-2.5 text-[13px] text-b4l-text-muted">
              <span>
                {filtrate.length} {filtrate.length === 1 ? 'comandă' : 'comenzi'} ·{' '}
                <span className="font-semibold text-b4l-text">{lei(totalCuTva)} lei</span>{' '}
                {etichetaTotal}, cu TVA ({lei(totalFaraTva)} fără TVA
                {stareTotal ? '' : '; fără ciorne, respinse, anulate'})
              </span>
              <Pagination
                page={pag.page}
                pageSize={pag.pageSize}
                total={pag.total}
                onPageChange={pag.setPage}
                onPageSizeChange={pag.setPageSize}
                itemLabel="comenzi"
              />
            </div>
          </>
        )}
      </section>

      {viewer && (
        <SpvInvoiceViewer
          inboundId={viewer.inboundId}
          orderId={viewer.orderId}
          docId={viewer.docId}
          onClose={() => setViewer(null)}
        />
      )}
      <SpvDebugModal order={debugFor} onClose={() => setDebugFor(null)} />
    </div>
  )
}
